import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { openBlobInNewTab, downloadBlob } from '../utils/fileUtils';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '../components/ui/button';
import {
  ClipboardCheck, User, Save, FileDown, Plus, Trash2, X, Check, Pen,
  Lock, Shield, ChevronRight, AlertTriangle, Send, Eye, FileText,
  CheckSquare, Square, ChevronDown, ChevronUp, CheckCircle, Mail, Phone, GraduationCap, Briefcase, CreditCard
} from 'lucide-react';
import VoiceInput from '../components/VoiceInput';

const SECTIONS_STRUCTURE = [
  {
    id: "vnm_selle", titre: "SEANCE DE DECOUVERTE EN VNM A SELLE", duree: "45 Min",
    items: [
      { id: "briefing_vnm_selle", titre: "BRIEFING ET DEBRIEFING VNM A SELLE", description: "Être capable de réaliser un briefing pour un groupe de 4 pratiquants et de leur transmettre les consignes de navigation et de sécurité. Au retour sur terre, être capable de débriefer sur l'encadrement de sa séance de découverte." },
      { id: "encadrement_vnm_selle", titre: "ENCADREMENT RANDONNEE VNM A SELLE", description: "Être capable de prendre en charge un groupe de 4 VNM en tenant compte des contraintes de sécurité, de la zone de pratique et des conditions environnementales." }
    ]
  },
  {
    id: "engins_tractes", titre: "SEANCE ENGINS TRACTES (E.T)", duree: "10 Min Max",
    items: [
      { id: "seance_engins_tractes", titre: "SEANCE ENGINS TRACTES (E.T)", description: "Être capable de tracter un engin tracté avec 2 pratiquants (1 bouée) sur un parcours imposé. À l'aller, 4 traversées de sillage alternées puis 1 demi-tour. Au retour, 4 traversées de sillage alternées. Arrêt avec les E.T. dans le sillage." }
    ]
  },
  {
    id: "secours", titre: "SEANCE DE MISE EN SITUATION DE SECOURS", duree: "25 Min Max",
    choix_support: true, supports: ["VNM à Selle", "Bateau"],
    items: [
      { id: "secours_support", titre: "MISE EN SITUATION DE SECOURS", description: "Être capable de réaliser une séance de mise en situation de secours. Intervention adaptée et sécurisée auprès du pratiquant en difficulté et démonstration de la connaissance des procédures d'urgence et des consignes de mise en sécurité du groupe." }
    ]
  },
  {
    id: "disciplines_associees", titre: "SEANCE D'INITIATION EN DISCIPLINES ASSOCIEES", duree: "25 Min Max",
    choix_support: true, supports: ["E.S.H.", "P.N.M.", "Jet à Bras"],
    items: [
      { id: "initiation_discipline", titre: "INITIATION DISCIPLINE ASSOCIEE", description: "Être capable de préparer le matériel, d'équiper son pratiquant, expliquer les règles de sécurité (briefing). Pratique de la séance en sécurité." }
    ]
  }
];

const ALL_ITEMS = SECTIONS_STRUCTURE.flatMap(s => s.items);

export default function GrilleEPMSP({ user }) {
  const [stagiaires, setStagiaires] = useState([]);
  const [selectedStagiaire, setSelectedStagiaire] = useState(null);
  const [grilles, setGrilles] = useState([]);
  const [currentGrille, setCurrentGrille] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Carnet évaluateurs
  const [evaluateursCarnet, setEvaluateursCarnet] = useState([]);
  const [expandedEvaluateur, setExpandedEvaluateur] = useState(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [pendingSignatureData, setPendingSignatureData] = useState(null);
  const signatureRef = useRef(null);

  // Signature candidat
  const [candidatSignature, setCandidatSignature] = useState(null);
  const [showCandidatSignPad, setShowCandidatSignPad] = useState(false);
  const candidatSignRef = useRef(null);

  // Form state
  const [numeroEvaluation, setNumeroEvaluation] = useState(1);
  const [supportSecours, setSupportSecours] = useState('');
  const [supportDiscipline, setSupportDiscipline] = useState('');
  const [evaluations, setEvaluations] = useState({});
  const [selectedEvaluateurs, setSelectedEvaluateurs] = useState([]);
  const [dateEvaluation, setDateEvaluation] = useState(new Date().toISOString().split('T')[0]);
  const [statutGrille, setStatutGrille] = useState('brouillon');
  const [firstEvalData, setFirstEvalData] = useState(null);

  // Tableau récapitulatif
  const [allGrilles, setAllGrilles] = useState([]);
  const [selectedForSend, setSelectedForSend] = useState([]);
  const [showRecapTable, setShowRecapTable] = useState(true);
  const [sendingResults, setSendingResults] = useState(false);

  useEffect(() => {
    fetchStagiaires();
    fetchEvaluateursCarnet();
    fetchAllGrilles();
  }, []);

  const fetchStagiaires = async () => {
    try {
      const res = await axios.get('/stagiaires');
      const data = Array.isArray(res.data) ? res.data : [];
      // Normaliser: si nom/prenom manquants, utiliser user_info
      const normalized = data.map(s => ({
        ...s,
        nom: s.nom || s.user_info?.nom || '',
        prenom: s.prenom || s.user_info?.prenom || ''
      }));
      setStagiaires(normalized);
    } catch { toast.error('Erreur chargement stagiaires'); }
    finally { setLoading(false); }
  };

  const fetchEvaluateursCarnet = async () => {
    try {
      const res = await axios.get('/evaluateurs');
      setEvaluateursCarnet(Array.isArray(res.data) ? res.data : []);
    } catch { /* ignore */ }
  };

  const fetchAllGrilles = async () => {
    try {
      const res = await axios.get('/epmsp/grille/all/list');
      setAllGrilles(Array.isArray(res.data) ? res.data : []);
    } catch { /* ignore */ }
  };

  const fetchGrilles = useCallback(async (stagiaireId) => {
    try {
      const res = await axios.get(`/epmsp/grille/stagiaire/${stagiaireId}`);
      const data = Array.isArray(res.data) ? res.data : [];
      setGrilles(data);
      return data;
    } catch { setGrilles([]); return []; }
  }, []);

  const handleSelectStagiaire = async (stag) => {
    setSelectedStagiaire(stag);
    const data = await fetchGrilles(stag.user_id);
    if (data.length > 0) {
      // Charger la dernière grille existante
      loadGrille(data[data.length - 1], data);
    } else {
      resetForm();
    }
  };

  const resetForm = () => {
    setCurrentGrille(null);
    setNumeroEvaluation(1);
    setSupportSecours('');
    setSupportDiscipline('');
    setEvaluations({});
    setSelectedEvaluateurs([]);
    setDateEvaluation(new Date().toISOString().split('T')[0]);
    setStatutGrille('brouillon');
    setFirstEvalData(null);
    setCandidatSignature(null);
  };

  const loadGrille = (grille, list = grilles) => {
    setCurrentGrille(grille);
    setNumeroEvaluation(grille.numero_evaluation || 1);
    setSupportSecours(grille.support_secours || '');
    setSupportDiscipline(grille.support_discipline || '');
    setEvaluations(grille.evaluations || {});
    setSelectedEvaluateurs(grille.evaluateurs || []);
    setDateEvaluation(grille.date_evaluation || new Date().toISOString().split('T')[0]);
    setStatutGrille(grille.statut_grille || 'brouillon');
    setCandidatSignature(grille.candidat_signature || null);

    // If 2nd eval, load 1st eval data for reference
    if (grille.numero_evaluation === 2) {
      const firstEval = list.find(g => g.numero_evaluation === 1);
      setFirstEvalData(firstEval?.evaluations || null);
    } else {
      setFirstEvalData(null);
    }
  };

  const startSecondEvaluation = () => {
    const firstEval = grilles.find(g => g.numero_evaluation === 1);
    if (!firstEval) {
      toast.error("Aucune 1ère évaluation trouvée");
      return;
    }
    const firstEvals = firstEval.evaluations || {};
    setFirstEvalData(firstEvals);

    // Pre-fill with acquis items from 1st eval
    const preFilledEvals = {};
    ALL_ITEMS.forEach(item => {
      if (firstEvals[item.id]?.statut === 'acquis') {
        preFilledEvals[item.id] = { statut: 'acquis', remarques: '', from_first_eval: true };
      }
    });

    setCurrentGrille(null);
    setNumeroEvaluation(2);
    setEvaluations(preFilledEvals);
    setSupportSecours(firstEval.support_secours || '');
    setSupportDiscipline(firstEval.support_discipline || '');
    setSelectedEvaluateurs([]);
    setDateEvaluation(new Date().toISOString().split('T')[0]);
    setStatutGrille('brouillon');
  };

  const updateEvaluation = (itemId, field, value) => {
    if (statutGrille === 'valide') return;
    // Don't allow changing items that were acquis in 1st eval
    if (evaluations[itemId]?.from_first_eval && field === 'statut') return;
    setEvaluations(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: value }
    }));
  };

  const addEvaluateurFromCarnet = (ev) => {
    if (statutGrille === 'valide') return;
    if (selectedEvaluateurs.find(e => e.evaluateur_id === ev.evaluateur_id)) return;
    if (!ev.signature) {
      // Open signature pad to collect signature before adding
      setPendingSignatureData({ evaluateur_id: ev.evaluateur_id, nom: ev.nom, prenom: ev.prenom, action: 'sign_and_add' });
      setShowSignaturePad(true);
      return;
    }
    setSelectedEvaluateurs(prev => [...prev, { evaluateur_id: ev.evaluateur_id, nom: ev.nom, prenom: ev.prenom, signature: ev.signature }]);
  };

  const removeEvaluateur = (evalId) => {
    if (statutGrille === 'valide') return;
    setSelectedEvaluateurs(prev => prev.filter(e => e.evaluateur_id !== evalId));
  };

  const validateSupports = () => {
    if (!supportSecours) {
      toast.error('Veuillez choisir le support pour la séance de secours (VNM à Selle ou Bateau)');
      return false;
    }
    if (!supportDiscipline) {
      toast.error('Veuillez choisir le support pour les disciplines associées (E.S.H., P.N.M. ou Jet à Bras)');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!selectedStagiaire || statutGrille === 'valide') return;
    if (!validateSupports()) return;
    setSaving(true);
    try {
      const payload = {
        stagiaire_id: selectedStagiaire.user_id,
        grille_id: currentGrille?.grille_id || undefined,
        numero_evaluation: numeroEvaluation,
        support_secours: supportSecours,
        support_discipline: supportDiscipline,
        evaluations,
        evaluateurs: selectedEvaluateurs,
        date_evaluation: dateEvaluation,
        candidat_signature: candidatSignature || undefined
      };
      const res = await axios.post('/epmsp/grille', payload);
      toast.success('Grille enregistrée (brouillon)');
      setCurrentGrille({ ...currentGrille, grille_id: res.data.grille_id, resultat_global: res.data.resultat_global, statut_grille: 'brouillon' });
      fetchGrilles(selectedStagiaire.user_id);
      fetchAllGrilles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur enregistrement');
    } finally { setSaving(false); }
  };

  const handleValider = async () => {
    if (!selectedStagiaire) return;
    if (!validateSupports()) return;
    if (!window.confirm('Valider cette grille ? Elle ne sera plus modifiable.')) return;
    try {
      // Toujours enregistrer l'état actuel (modifs faites après le brouillon) avant de valider
      const payload = {
        stagiaire_id: selectedStagiaire.user_id,
        grille_id: currentGrille?.grille_id || undefined,
        numero_evaluation: numeroEvaluation,
        support_secours: supportSecours,
        support_discipline: supportDiscipline,
        evaluations,
        evaluateurs: selectedEvaluateurs,
        date_evaluation: dateEvaluation,
        candidat_signature: candidatSignature || undefined
      };
      const saveRes = await axios.post('/epmsp/grille', payload);
      const grilleId = saveRes.data.grille_id;
      setCurrentGrille({ ...currentGrille, grille_id: grilleId, resultat_global: saveRes.data.resultat_global, statut_grille: 'brouillon' });
      await axios.post(`/epmsp/grille/${grilleId}/valider`);
      toast.success('Grille validée et gelée');
      setStatutGrille('valide');
      fetchGrilles(selectedStagiaire.user_id);
      fetchAllGrilles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur validation');
    }
  };

  const handleDownloadPDF = async (grilleId, nomOverride) => {
    try {
      const res = await axios.get(`/pdf/grille-epmsp/${grilleId}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const nom = nomOverride || selectedStagiaire?.nom || 'stagiaire';
      downloadBlob(blob, `grille_epmsp_${nom}_eval${numeroEvaluation}.pdf`);
    } catch { toast.error('Erreur génération PDF'); }
  };

  const handlePreviewPDF = async (grilleId) => {
    try {
      toast.info('Chargement de la prévisualisation...');
      const res = await axios.get(`/pdf/grille-epmsp/${grilleId}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      openBlobInNewTab(blob);
    } catch { toast.error('Erreur prévisualisation PDF'); }
  };

  const handleDeleteGrille = async (grilleId) => {
    if (!window.confirm('Supprimer cette grille ?')) return;
    try {
      await axios.delete(`/epmsp/grille/${grilleId}`);
      toast.success('Grille supprimée');
      fetchGrilles(selectedStagiaire.user_id);
      fetchAllGrilles();
      if (currentGrille?.grille_id === grilleId) resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur suppression');
    }
  };

  // Inline signature: sign for an evaluateur without signature, then add to grille
  const handleSignEvaluateur = (ev) => {
    setPendingSignatureData({ evaluateur_id: ev.evaluateur_id, nom: ev.nom, prenom: ev.prenom, action: 'sign_and_add' });
    setShowSignaturePad(true);
  };

  const saveSignature = async () => {
    if (signatureRef.current?.isEmpty()) { toast.error('Veuillez dessiner la signature'); return; }
    const sigData = signatureRef.current.toDataURL();

    if (pendingSignatureData?.action === 'sign_and_add') {
      try {
        // Save signature to the evaluateur profile
        await axios.put(`/evaluateurs/${pendingSignatureData.evaluateur_id}`, { signature: sigData });
        toast.success('Signature enregistrée');
        // Refresh the carnet list
        fetchEvaluateursCarnet();
        // Add to grille with signature
        if (!selectedEvaluateurs.find(e => e.evaluateur_id === pendingSignatureData.evaluateur_id)) {
          setSelectedEvaluateurs(prev => [...prev, {
            evaluateur_id: pendingSignatureData.evaluateur_id,
            nom: pendingSignatureData.nom,
            prenom: pendingSignatureData.prenom,
            signature: sigData
          }]);
        }
      } catch { toast.error('Erreur enregistrement signature'); }
    }
    setShowSignaturePad(false);
    setPendingSignatureData(null);
  };

  const getResultatLabel = (resultat) => {
    if (resultat === 'acquis') return { label: 'ACQUIS', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' };
    if (resultat === 'non_acquis') return { label: 'NON ACQUIS', cls: 'bg-red-100 text-red-800 border-red-300' };
    return { label: 'EN COURS', cls: 'bg-amber-100 text-amber-800 border-amber-300' };
  };

  const isReadOnly = statutGrille === 'valide';
  const canReopen = isReadOnly && ['centre', 'direction'].includes(user?.role) && currentGrille?.grille_id
    && (currentGrille?.numero_evaluation || numeroEvaluation) === 1 && !grilles.some(g => g.numero_evaluation === 2);

  const handleReopen = async () => {
    if (!window.confirm('Remettre cette grille en brouillon ? Elle redeviendra modifiable et les résultats des épreuves issus de cette grille seront retirés jusqu\'à la prochaine validation.')) return;
    try {
      await axios.post(`/epmsp/grille/${currentGrille.grille_id}/remettre-brouillon`);
      toast.success('Grille remise en brouillon');
      setStatutGrille('brouillon');
      setCurrentGrille({ ...currentGrille, statut_grille: 'brouillon' });
      fetchGrilles(selectedStagiaire.user_id);
      fetchAllGrilles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };
  const has1stEval = grilles.some(g => g.numero_evaluation === 1);
  const has2ndEval = grilles.some(g => g.numero_evaluation === 2);
  const first1stEval = grilles.find(g => g.numero_evaluation === 1);
  const needs2ndEval = has1stEval && !has2ndEval && first1stEval?.statut_grille === 'valide' && first1stEval?.resultat_global !== 'acquis';

  // === Tableau récapitulatif : regrouper les grilles par stagiaire ===
  const recapByStagiaire = {};
  allGrilles.forEach(g => {
    const sid = g.stagiaire_id;
    if (!recapByStagiaire[sid]) {
      recapByStagiaire[sid] = {
        stagiaire_id: sid,
        nom: g.stagiaire_nom || '',
        prenom: g.stagiaire_prenom || '',
        grilles: []
      };
    }
    recapByStagiaire[sid].grilles.push(g);
  });
  // Ajouter les stagiaires sans grille
  stagiaires.forEach(s => {
    if (!recapByStagiaire[s.user_id]) {
      recapByStagiaire[s.user_id] = {
        stagiaire_id: s.user_id,
        nom: s.nom || '',
        prenom: s.prenom || '',
        grilles: []
      };
    }
  });
  const recapList = Object.values(recapByStagiaire).sort((a, b) => `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`));

  const toggleSelectForSend = (stagId) => {
    setSelectedForSend(prev =>
      prev.includes(stagId) ? prev.filter(id => id !== stagId) : [...prev, stagId]
    );
  };

  const toggleSelectAll = () => {
    const selectableIds = recapList
      .filter(r => {
        const hasValidated = r.grilles.some(g => g.statut_grille === 'valide');
        const hasUnsent = r.grilles.some(g => g.statut_grille === 'valide' && !g.resultats_envoyes);
        return hasValidated && hasUnsent;
      })
      .map(r => r.stagiaire_id);
    if (selectedForSend.length === selectableIds.length && selectableIds.length > 0) {
      setSelectedForSend([]);
    } else {
      setSelectedForSend(selectableIds);
    }
  };

  const handleSendResults = async (ids) => {
    const targetIds = ids || selectedForSend;
    if (targetIds.length === 0) {
      toast.error('Sélectionnez au moins un stagiaire');
      return;
    }
    setSendingResults(true);
    try {
      const res = await axios.post('/epmsp/grille/send-results', { stagiaire_ids: targetIds });
      toast.success(res.data.message || 'Résultats envoyés');
      if (!ids) setSelectedForSend([]);
      fetchAllGrilles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur envoi');
    } finally {
      setSendingResults(false);
    }
  };

  const handleQuickValidate = async (grilleId) => {
    if (!window.confirm('⚠️ Valider et geler cette grille ?\n\nUne fois validée, la grille ne sera plus modifiable. Cette action est irréversible.')) return;
    try {
      await axios.post(`/epmsp/grille/${grilleId}/valider`);
      toast.success('Grille validée et gelée avec succès');
      fetchAllGrilles();
      if (selectedStagiaire) fetchGrilles(selectedStagiaire.user_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur validation');
    }
  };

  const handleViewGrilleFromRecap = async (stagId) => {
    const stag = stagiaires.find(s => s.user_id === stagId);
    if (stag) {
      setSelectedStagiaire(stag);
      const data = await fetchGrilles(stagId);
      if (data.length > 0) {
        loadGrille(data[data.length - 1], data);
      }
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-slate-500">Chargement...</div></div>;

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="grille-epmsp-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6 text-[#8DC63F]" />
            Grille d'Évaluation EPMSP
          </h1>
          <p className="text-sm text-slate-500 mt-1">Critères d'évaluation des épreuves</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* ===== LEFT PANEL ===== */}
        <div className="lg:col-span-1 space-y-4">
          {/* Stagiaire selection */}
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stagiaire-selector">
            <h3 className="font-semibold text-slate-800 mb-3 text-sm">Sélectionner un stagiaire</h3>
            <div className="space-y-2">
              {stagiaires.map(stag => {
                // Vérifier si ce stagiaire a déjà toutes ses grilles validées (acquis au 1er passage)
                const stagGrilles = allGrilles.filter(g => g.stagiaire_id === stag.user_id);
                const hasValidatedAcquis = stagGrilles.some(g => g.statut_grille === 'valide' && g.resultat_global === 'acquis');
                const isSelected = selectedStagiaire?.user_id === stag.user_id;

                return (
                  <button key={stag.user_id} onClick={() => handleSelectStagiaire(stag)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                      isSelected
                        ? 'bg-[#8DC63F]/10 border border-[#8DC63F] text-[#8DC63F] font-medium'
                        : hasValidatedAcquis
                          ? 'bg-emerald-50 text-emerald-600 border border-emerald-200 opacity-70'
                          : 'bg-slate-50 hover:bg-slate-100 text-slate-700'
                    }`} data-testid={`select-stagiaire-${stag.user_id}`}>
                    <div className="flex items-center justify-between">
                      <span><User className="h-4 w-4 inline mr-2" />{stag.prenom} {stag.nom}</span>
                      {hasValidatedAcquis && <CheckCircle className="h-4 w-4 text-emerald-500" />}
                    </div>
                  </button>
                );
              })}
              {stagiaires.length === 0 && <p className="text-xs text-slate-400">Aucun stagiaire</p>}
            </div>
          </div>

          {/* Existing grilles */}
          {selectedStagiaire && (() => {
            const stagHasAcquis = allGrilles.some(g => g.stagiaire_id === selectedStagiaire.user_id && g.statut_grille === 'valide' && g.resultat_global === 'acquis');
            return (
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="grilles-list">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-800 text-sm">Grilles</h3>
                {!stagHasAcquis && (
                  <div className="flex gap-1">
                    {!has1stEval && (
                      <Button size="sm" variant="outline" onClick={() => resetForm()} className="h-7 text-xs" data-testid="new-grille-btn">
                        <Plus className="h-3 w-3 mr-1" /> 1ère
                      </Button>
                    )}
                    {needs2ndEval && (
                      <Button size="sm" variant="outline" onClick={startSecondEvaluation} className="h-7 text-xs border-orange-300 text-orange-600 hover:bg-orange-50" data-testid="new-2nd-eval-btn">
                        <Plus className="h-3 w-3 mr-1" /> 2ème passage (rattrapage)
                      </Button>
                    )}
                  </div>
                )}
                {stagHasAcquis && <span className="text-xs text-emerald-600 font-medium flex items-center gap-1"><CheckCircle className="h-3.5 w-3.5" /> EPMSP acquis</span>}
              </div>
              <div className="space-y-2">
                {grilles.map(g => {
                  const r = getResultatLabel(g.resultat_global);
                  const isValidated = g.statut_grille === 'valide';
                  return (
                    <div key={g.grille_id}
                      className={`p-2.5 rounded-lg border text-xs cursor-pointer transition-all ${
                        currentGrille?.grille_id === g.grille_id ? 'border-[#8DC63F] bg-[#8DC63F]/5' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => loadGrille(g)}
                      data-testid={`grille-item-${g.grille_id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          {isValidated ? <Lock className="h-3 w-3 text-emerald-600" /> : <Pen className="h-3 w-3 text-amber-500" />}
                          <span className="font-medium text-slate-700">
                            {g.numero_evaluation === 1 ? '1ère' : '2ème'} Éval.
                          </span>
                        </div>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${r.cls}`}>{r.label}</span>
                      </div>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-slate-400">{g.date_evaluation || 'N/A'}</span>
                        <div className="flex items-center gap-1">
                          <span className={`px-1 py-0.5 rounded text-[9px] ${isValidated ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                            {isValidated ? 'Validée' : 'Brouillon'}
                          </span>
                          {isValidated && (
                            <button onClick={(e) => { e.stopPropagation(); handleDownloadPDF(g.grille_id); }} className="text-blue-600 hover:text-blue-800 ml-1" data-testid={`download-pdf-${g.grille_id}`}>
                              <FileDown className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {!isValidated && (
                            <button onClick={(e) => { e.stopPropagation(); handleDeleteGrille(g.grille_id); }} className="text-red-500 hover:text-red-700 ml-1" data-testid={`delete-grille-${g.grille_id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {grilles.length === 0 && <p className="text-xs text-slate-400">Aucune grille</p>}
              </div>
            </div>
          );
          })()}

          {/* Carnet évaluateurs */}
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="carnet-evaluateurs">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-1.5">
                <Shield className="h-4 w-4 text-slate-400" /> Évaluateurs
              </h3>
              <a href="/evaluateurs" className="text-xs text-blue-600 hover:text-blue-800 hover:underline" data-testid="link-evaluateurs-tab">
                Gérer
              </a>
            </div>

            <div className="space-y-1.5">
              {evaluateursCarnet.map(ev => {
                const isAlreadyAdded = selectedEvaluateurs.some(e => e.evaluateur_id === ev.evaluateur_id);
                const isExpanded = expandedEvaluateur === ev.evaluateur_id;
                return (
                  <div key={ev.evaluateur_id} className={`rounded-lg transition-all overflow-hidden ${isAlreadyAdded ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50 border border-transparent hover:border-slate-200'}`}>
                    {/* Header row - clickable */}
                    <div
                      className="flex items-center justify-between p-2 cursor-pointer"
                      onClick={() => setExpandedEvaluateur(isExpanded ? null : ev.evaluateur_id)}
                      data-testid={`eval-header-${ev.evaluateur_id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[9px] font-bold text-slate-600 flex-shrink-0">
                          {(ev.prenom?.[0] || '').toUpperCase()}{(ev.nom?.[0] || '').toUpperCase()}
                        </div>
                        <span className="text-xs text-slate-700 truncate font-medium">{ev.prenom} {ev.nom}</span>
                        {isAlreadyAdded && <Check className="h-3 w-3 text-emerald-600 flex-shrink-0" />}
                      </div>
                      <div className="flex items-center gap-1.5">
                        {!isAlreadyAdded && statutGrille !== 'valide' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); addEvaluateurFromCarnet(ev); }}
                            className="w-6 h-6 rounded-full bg-[#8DC63F] hover:bg-[#7db336] text-white flex items-center justify-center transition-colors shadow-sm"
                            title={ev.signature ? 'Ajouter à la grille' : 'Signer et ajouter'}
                            data-testid={`quick-add-eval-${ev.evaluateur_id}`}
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
                      </div>
                    </div>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="px-3 pb-3 space-y-2 border-t border-slate-200/60 pt-2">
                        {/* Contact */}
                        {(ev.email || ev.telephone) && (
                          <div className="space-y-1">
                            {ev.email && <p className="text-[11px] text-slate-500 flex items-center gap-1.5"><Mail className="h-3 w-3 text-slate-400" />{ev.email}</p>}
                            {ev.telephone && <p className="text-[11px] text-slate-500 flex items-center gap-1.5"><Phone className="h-3 w-3 text-slate-400" />{ev.telephone}</p>}
                          </div>
                        )}
                        {/* Diplômes & Expérience */}
                        {ev.diplomes && <p className="text-[11px] text-slate-500 flex items-center gap-1.5"><GraduationCap className="h-3 w-3 text-slate-400" />{ev.diplomes}</p>}
                        {ev.experience && <p className="text-[11px] text-slate-500 flex items-center gap-1.5"><Briefcase className="h-3 w-3 text-slate-400" />{ev.experience}</p>}
                        {/* Carte professionnelle */}
                        <div className="flex items-center gap-1.5">
                          <CreditCard className="h-3 w-3 text-slate-400" />
                          {ev.numero_carte_pro ? (
                            <span className="text-[11px] text-slate-600 font-medium">
                              {ev.numero_carte_pro}
                              {ev.date_expiration_carte && <span className="text-slate-400 font-normal ml-1">(exp. {ev.date_expiration_carte})</span>}
                            </span>
                          ) : (
                            <span className="text-[11px] text-amber-500 italic">Carte pro non renseignée</span>
                          )}
                        </div>
                        {/* Signature */}
                        <div className="flex items-center gap-2">
                          {ev.signature ? (
                            <div className="flex items-center gap-2">
                              <img src={ev.signature} alt="" className="h-8 w-14 object-contain border rounded bg-white" />
                              <span className="text-[10px] text-emerald-600">Signature OK</span>
                            </div>
                          ) : (
                            <span className="text-[11px] text-amber-500 italic flex items-center gap-1"><Pen className="h-3 w-3" /> Pas de signature</span>
                          )}
                        </div>
                        {/* Action buttons */}
                        {!isAlreadyAdded && statutGrille !== 'valide' && (
                          <div className="pt-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); addEvaluateurFromCarnet(ev); }}
                              className="w-full text-center py-1.5 rounded-md text-xs font-medium bg-[#8DC63F] text-white hover:bg-[#7db336] transition-colors"
                              data-testid={`add-eval-${ev.evaluateur_id}`}
                            >
                              <Plus className="h-3.5 w-3.5 inline mr-1" />
                              {ev.signature ? 'Ajouter à la grille' : 'Signer et ajouter'}
                            </button>
                          </div>
                        )}
                        {isAlreadyAdded && (
                          <p className="text-[11px] text-emerald-600 font-medium text-center pt-1">Déjà ajouté à la grille</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {evaluateursCarnet.length === 0 && (
                <div className="text-center py-2">
                  <p className="text-[11px] text-slate-400 mb-1">Aucun évaluateur</p>
                  <a href="/evaluateurs" className="text-[11px] text-blue-600 hover:underline">Créer des évaluateurs</a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ===== RIGHT PANEL - FORM ===== */}
        <div className="lg:col-span-3">
          {!selectedStagiaire ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <ClipboardCheck className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Sélectionnez un stagiaire pour commencer l'évaluation</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Header info */}
              <div className={`bg-white rounded-xl border p-4 ${isReadOnly ? 'border-emerald-300 bg-emerald-50/30' : 'border-slate-200'}`} data-testid="grille-header">
                {/* Status banner */}
                {isReadOnly && (
                  <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-emerald-100 rounded-lg text-emerald-800 text-xs font-medium">
                    <Lock className="h-4 w-4" /> Grille validée - Non modifiable
                    {canReopen && (
                      <Button size="sm" variant="outline" onClick={handleReopen} className="ml-auto h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="reopen-grille-btn">
                        <Pen className="h-3 w-3 mr-1" /> Remettre en brouillon
                      </Button>
                    )}
                  </div>
                )}
                {!isReadOnly && currentGrille?.grille_id && (
                  <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-amber-50 rounded-lg text-amber-700 text-xs font-medium">
                    <Pen className="h-4 w-4" /> Brouillon - Modifiable avant validation
                  </div>
                )}
                {/* 2nd eval banner */}
                {numeroEvaluation === 2 && (
                  <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-orange-100 rounded-lg text-orange-800 text-xs font-bold" data-testid="second-eval-banner">
                    <AlertTriangle className="h-4 w-4" /> 2ème ÉVALUATION (RATTRAPAGE) — Les épreuves acquises au 1er passage sont conservées
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Candidat</label>
                    <p className="text-sm font-semibold text-slate-800" data-testid="candidat-name">
                      {selectedStagiaire.prenom} {selectedStagiaire.nom}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">N° Évaluation</label>
                    <div className={`px-3 py-1.5 border rounded-lg text-sm font-bold ${
                      numeroEvaluation === 2 ? 'bg-orange-50 border-orange-300 text-orange-700' : 'bg-slate-50 border-slate-200 text-slate-700'
                    }`} data-testid="numero-evaluation-display">
                      {numeroEvaluation === 1 ? '1ère Évaluation' : '2ème Évaluation'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Date</label>
                    <input type="date" value={dateEvaluation} onChange={(e) => !isReadOnly && setDateEvaluation(e.target.value)}
                      className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm" disabled={isReadOnly} data-testid="date-evaluation-input" />
                  </div>
                </div>
              </div>

              {/* Evaluation sections */}
              {SECTIONS_STRUCTURE.map(section => (
                <div key={section.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`section-${section.id}`}>
                  <div className="bg-[#0F172A] px-4 py-2.5">
                    <h3 className="text-white font-bold text-sm">{section.titre} ({section.duree})</h3>
                  </div>

                  {section.choix_support && (
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">Support choisi :</label>
                      <div className="flex gap-3">
                        {section.supports.map(s => {
                          const val = section.id === 'secours' ? supportSecours : supportDiscipline;
                          const setter = section.id === 'secours' ? setSupportSecours : setSupportDiscipline;
                          return (
                            <label key={s} className="flex items-center gap-1.5 cursor-pointer" data-testid={`support-${section.id}-${s}`}>
                              <input type="radio" name={`support-${section.id}`} checked={val === s}
                                onChange={() => !isReadOnly && setter(s)} className="accent-[#8DC63F]" disabled={isReadOnly} />
                              <span className="text-sm text-slate-700">{s}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Table header */}
                  <div className="grid grid-cols-12 bg-slate-100 px-4 py-2 border-b border-slate-200 text-xs font-semibold text-slate-600">
                    <div className="col-span-5">Épreuve</div>
                    <div className="col-span-4">Remarques</div>
                    <div className="col-span-1 text-center">Acquis</div>
                    <div className="col-span-2 text-center">Non Acquis</div>
                  </div>

                  {section.items.map(item => {
                    const itemEval = evaluations[item.id] || {};
                    const isFromFirst = itemEval.from_first_eval;
                    const firstEvalStatus = firstEvalData?.[item.id]?.statut;

                    return (
                      <div key={item.id} className={`grid grid-cols-12 px-4 py-3 border-b border-slate-100 items-start ${isFromFirst ? 'bg-emerald-50/50' : ''}`} data-testid={`item-${item.id}`}>
                        <div className="col-span-5 pr-3">
                          <p className="text-xs font-bold text-slate-800">{item.titre}</p>
                          <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">{item.description}</p>
                          {isFromFirst && (
                            <span className="inline-block mt-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[9px] font-bold rounded">
                              Acquis (1er passage)
                            </span>
                          )}
                          {numeroEvaluation === 2 && firstEvalStatus === 'non_acquis' && !isFromFirst && (
                            <span className="inline-block mt-1 px-1.5 py-0.5 bg-orange-100 text-orange-700 text-[9px] font-bold rounded">
                              À réévaluer
                            </span>
                          )}
                        </div>
                        <div className="col-span-4 pr-3">
                          <div className="relative">
                            <textarea value={itemEval.remarques || ''} onChange={(e) => updateEvaluation(item.id, 'remarques', e.target.value)}
                              placeholder="Remarques... ou dictez avec le micro"
                              className="w-full px-2 py-1.5 pr-9 border border-slate-200 rounded text-xs resize-none h-16"
                              disabled={isReadOnly || isFromFirst} data-testid={`remarques-${item.id}`} />
                            {!isReadOnly && !isFromFirst && (
                              <VoiceInput
                                onTranscript={(text) => updateEvaluation(item.id, 'remarques', (itemEval.remarques || '') + (itemEval.remarques ? ' ' : '') + text)}
                                className="absolute top-1 right-1 w-6 h-6"
                              />
                            )}
                          </div>
                        </div>
                        <div className="col-span-1 flex justify-center pt-2">
                          <button onClick={() => updateEvaluation(item.id, 'statut', 'acquis')}
                            disabled={isReadOnly || isFromFirst}
                            className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center transition-all ${
                              itemEval.statut === 'acquis'
                                ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 hover:border-emerald-400 text-slate-300'
                            } ${(isReadOnly || isFromFirst) ? 'opacity-60 cursor-not-allowed' : ''}`} data-testid={`acquis-${item.id}`}>
                            <Check className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="col-span-2 flex justify-center pt-2">
                          <button onClick={() => updateEvaluation(item.id, 'statut', 'non_acquis')}
                            disabled={isReadOnly || isFromFirst}
                            className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center transition-all ${
                              itemEval.statut === 'non_acquis'
                                ? 'bg-red-500 border-red-500 text-white' : 'border-slate-300 hover:border-red-400 text-slate-300'
                            } ${(isReadOnly || isFromFirst) ? 'opacity-60 cursor-not-allowed' : ''}`} data-testid={`non-acquis-${item.id}`}>
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}

              {/* Résultat global */}
              <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="resultat-global">
                <h3 className="font-bold text-slate-800 mb-3 text-sm">RÉSULTAT DES ÉVALUATIONS</h3>
                {(() => {
                  const allEvaluated = ALL_ITEMS.every(item => evaluations[item.id]?.statut);
                  const allAcquis = ALL_ITEMS.every(item => evaluations[item.id]?.statut === 'acquis');
                  const r = allEvaluated ? (allAcquis ? getResultatLabel('acquis') : getResultatLabel('non_acquis')) : getResultatLabel('en_cours');
                  return (
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-600">Résultat :</span>
                      <span className={`px-3 py-1.5 rounded-lg text-sm font-bold border ${r.cls}`} data-testid="resultat-label">{r.label}</span>
                      {allEvaluated && allAcquis && <span className="text-emerald-600 text-xs">Toutes les épreuves sont validées</span>}
                      {!allEvaluated && <span className="text-amber-600 text-xs">Évaluez toutes les épreuves pour obtenir le résultat</span>}
                    </div>
                  );
                })()}
              </div>

              {/* Évaluateurs sélectionnés */}
              <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="evaluateurs-section">
                <h3 className="font-bold text-slate-800 mb-3 text-sm">ÉVALUATEUR(S) / JURY</h3>
                {selectedEvaluateurs.length === 0 && (
                  <p className="text-xs text-slate-400 mb-2">
                    Aucun évaluateur sélectionné. Utilisez le <strong>Carnet Évaluateurs</strong> dans le panneau gauche pour en ajouter.
                  </p>
                )}
                <div className="space-y-2">
                  {selectedEvaluateurs.map(ev => (
                    <div key={ev.evaluateur_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center gap-3">
                        {ev.signature && <img src={ev.signature} alt="" className="h-8 w-14 object-contain border rounded bg-white p-0.5" />}
                        <span className="text-sm font-medium text-slate-700">{ev.prenom} {ev.nom}</span>
                      </div>
                      {!isReadOnly && (
                        <button onClick={() => removeEvaluateur(ev.evaluateur_id)} className="text-red-400 hover:text-red-600">
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Signature du candidat */}
              <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="candidat-signature-section">
                <h3 className="font-bold text-slate-800 mb-3 text-sm flex items-center gap-2">
                  <Pen className="h-4 w-4 text-slate-400" />
                  SIGNATURE DU CANDIDAT
                </h3>
                {candidatSignature ? (
                  <div className="flex items-center gap-4">
                    <div className="border-2 border-emerald-200 rounded-lg p-2 bg-white">
                      <img src={candidatSignature} alt="Signature candidat" className="h-16 w-40 object-contain" data-testid="candidat-signature-preview" />
                    </div>
                    {!isReadOnly && (
                      <div className="flex flex-col gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => setShowCandidatSignPad(true)} className="text-xs" data-testid="change-candidat-signature-btn">
                          <Pen className="h-3 w-3 mr-1" /> Modifier
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setCandidatSignature(null)} className="text-xs text-red-500 border-red-200 hover:bg-red-50" data-testid="remove-candidat-signature-btn">
                          <Trash2 className="h-3 w-3 mr-1" /> Retirer
                        </Button>
                      </div>
                    )}
                    {isReadOnly && <span className="text-xs text-emerald-600 font-medium flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Signé</span>}
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <p className="text-xs text-slate-400">Aucune signature collectée</p>
                    {!isReadOnly && (
                      <Button size="sm" variant="outline" onClick={() => setShowCandidatSignPad(true)} className="text-xs border-[#8DC63F] text-[#8DC63F] hover:bg-[#8DC63F]/10" data-testid="collect-candidat-signature-btn">
                        <Pen className="h-3 w-3 mr-1" /> Collecter la signature
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex gap-3 justify-end flex-wrap" data-testid="action-buttons">
                {!isReadOnly && (
                  <>
                    <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-sm" data-testid="save-grille-btn">
                      <Save className="h-4 w-4 mr-2" />
                      {saving ? 'Enregistrement...' : 'Enregistrer (brouillon)'}
                    </Button>
                    <Button onClick={handleValider} className="bg-emerald-600 hover:bg-emerald-700 text-sm" data-testid="valider-grille-btn">
                      <Lock className="h-4 w-4 mr-2" />
                      Valider et geler
                    </Button>
                  </>
                )}
                {isReadOnly && currentGrille?.grille_id && (
                  <Button onClick={() => handleDownloadPDF(currentGrille.grille_id)} className="bg-blue-600 hover:bg-blue-700 text-sm" data-testid="download-current-pdf-btn">
                    <FileDown className="h-4 w-4 mr-2" />
                    Télécharger PDF
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ===== TABLEAU RÉCAPITULATIF ===== */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="recap-table">
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <button onClick={() => setShowRecapTable(!showRecapTable)} className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-[#8DC63F]" />
              <h2 className="text-lg font-bold text-slate-900">Tableau récapitulatif EPMSP</h2>
              <span className="text-xs text-slate-400 ml-2">({recapList.length} stagiaire{recapList.length > 1 ? 's' : ''})</span>
              {showRecapTable ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
            </button>
            {showRecapTable && selectedForSend.length > 0 && (
              <Button onClick={() => handleSendResults()} disabled={sendingResults} className="bg-blue-600 hover:bg-blue-700 text-sm" data-testid="send-results-btn">
                <Send className="h-4 w-4 mr-2" />
                {sendingResults ? 'Envoi...' : `Envoyer résultats (${selectedForSend.length})`}
              </Button>
            )}
          </div>
        </div>

        {showRecapTable && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left p-3 w-8">
                    <button onClick={toggleSelectAll} className="text-slate-400 hover:text-slate-600" data-testid="select-all-btn">
                      {selectedForSend.length > 0 && selectedForSend.length === recapList.filter(r => r.grilles.some(g => g.statut_grille === 'valide')).length
                        ? <CheckSquare className="h-4 w-4 text-blue-600" />
                        : <Square className="h-4 w-4" />}
                    </button>
                  </th>
                  <th className="text-left p-3 font-semibold text-slate-700">Stagiaire</th>
                  <th className="text-center p-3 font-semibold text-slate-700">1ère Éval.</th>
                  <th className="text-center p-3 font-semibold text-slate-700">Rattrapage</th>
                  <th className="text-center p-3 font-semibold text-slate-700">Résultat final</th>
                  <th className="text-center p-3 font-semibold text-slate-700">Envoyé</th>
                  <th className="text-right p-3 font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {recapList.length === 0 ? (
                  <tr><td colSpan={7} className="p-6 text-center text-slate-400">Aucun stagiaire</td></tr>
                ) : recapList.map((r, idx) => {
                  const eval1 = r.grilles.find(g => g.numero_evaluation === 1);
                  const eval2 = r.grilles.find(g => g.numero_evaluation === 2);
                  const latestGrille = eval2 || eval1;
                  const hasValidated = r.grilles.some(g => g.statut_grille === 'valide');
                  const resultat = latestGrille?.resultat_global;
                  const rLabel = getResultatLabel(resultat);
                  const resultatsEnvoyes = r.grilles.some(g => g.resultats_envoyes);
                  const hasUnsentValidated = r.grilles.some(g => g.statut_grille === 'valide' && !g.resultats_envoyes);
                  const canSelect = hasValidated && hasUnsentValidated;
                  const isActive = selectedStagiaire?.user_id === r.stagiaire_id;
                  const rowBg = isActive ? 'bg-[#8DC63F]/10' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50';

                  const renderEvalBadge = (g) => {
                    if (!g) return <span className="text-xs text-slate-300">-</span>;
                    const isVal = g.statut_grille === 'valide';
                    const res = getResultatLabel(g.resultat_global);
                    return (
                      <div className="flex flex-col items-center gap-0.5">
                        <span className={`px-2 py-0.5 rounded text-[11px] font-bold border ${res.cls}`}>{res.label}</span>
                        <span className={`text-[10px] font-medium ${isVal ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {isVal ? 'Validée' : 'Brouillon'}
                        </span>
                      </div>
                    );
                  };

                  return (
                    <tr key={r.stagiaire_id} className={`${rowBg} hover:bg-blue-50/50 border-b border-slate-100 transition-colors`} data-testid={`recap-row-${r.stagiaire_id}`}>
                      <td className="p-3">
                        {canSelect ? (
                          <button onClick={() => toggleSelectForSend(r.stagiaire_id)} data-testid={`select-${r.stagiaire_id}`}>
                            {selectedForSend.includes(r.stagiaire_id)
                              ? <CheckSquare className="h-4 w-4 text-blue-600" />
                              : <Square className="h-4 w-4 text-slate-300" />}
                          </button>
                        ) : <Square className="h-4 w-4 text-slate-200 opacity-30" />}
                      </td>
                      <td className="p-3">
                        <span className="font-semibold text-slate-900">{r.prenom} {r.nom}</span>
                      </td>
                      <td className="p-3 text-center">{renderEvalBadge(eval1)}</td>
                      <td className="p-3 text-center">{renderEvalBadge(eval2)}</td>
                      <td className="p-3 text-center">
                        {latestGrille ? (
                          <span className={`px-2.5 py-1 rounded-md text-xs font-bold border ${rLabel.cls}`}>{rLabel.label}</span>
                        ) : <span className="text-xs text-slate-300 italic">Aucune grille</span>}
                      </td>
                      <td className="p-3 text-center">
                        {resultatsEnvoyes
                          ? <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-medium"><Check className="h-3.5 w-3.5" /> Oui</span>
                          : <span className="text-xs text-slate-300">-</span>}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-1.5">
                          {latestGrille && (
                            <button onClick={() => handleViewGrilleFromRecap(r.stagiaire_id)}
                              className="p-1.5 rounded-md hover:bg-blue-100 text-blue-600 transition-colors" title="Voir / Modifier"
                              data-testid={`view-${r.stagiaire_id}`}>
                              <Eye className="h-4 w-4" />
                            </button>
                          )}
                          {latestGrille && latestGrille.statut_grille === 'brouillon' && (
                            <button onClick={() => handleQuickValidate(latestGrille.grille_id)}
                              className="p-1.5 rounded-md hover:bg-emerald-100 text-emerald-600 transition-colors" title="Valider et geler"
                              data-testid={`validate-${r.stagiaire_id}`}>
                              <Lock className="h-4 w-4" />
                            </button>
                          )}
                          {latestGrille && (
                            <>
                              <button onClick={() => handlePreviewPDF(latestGrille.grille_id)}
                                className="p-1.5 rounded-md hover:bg-indigo-100 text-indigo-600 transition-colors" title="Prévisualiser PDF"
                                data-testid={`preview-pdf-${r.stagiaire_id}`}>
                                <FileText className="h-4 w-4" />
                              </button>
                              <button onClick={() => handleDownloadPDF(latestGrille.grille_id, r.nom)}
                                className="p-1.5 rounded-md hover:bg-violet-100 text-violet-600 transition-colors" title="Télécharger PDF"
                                data-testid={`pdf-${r.stagiaire_id}`}>
                                <FileDown className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {hasValidated && hasUnsentValidated && (
                            <button onClick={() => handleSendResults([r.stagiaire_id])}
                              className="p-1.5 rounded-md hover:bg-blue-100 text-blue-500 transition-colors" title="Envoyer résultats"
                              data-testid={`send-${r.stagiaire_id}`}>
                              <Send className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Signature Pad Modal - Évaluateur */}
      {showSignaturePad && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" data-testid="signature-modal">
          <div className="bg-white rounded-xl p-6 w-[500px] max-w-[95vw]">
            <h4 className="font-bold text-slate-800 mb-1">Signature de l'évaluateur</h4>
            <p className="text-xs text-slate-500 mb-3">{pendingSignatureData?.prenom} {pendingSignatureData?.nom}</p>
            <div className="border-2 border-slate-200 rounded-lg overflow-hidden bg-white">
              <SignatureCanvas ref={signatureRef} canvasProps={{ width: 460, height: 180, className: 'signature-canvas' }} backgroundColor="white" />
            </div>
            <div className="flex gap-2 mt-3">
              <Button onClick={saveSignature} className="bg-[#8DC63F] hover:bg-[#7db336] text-sm" data-testid="save-signature-btn">
                <Check className="h-4 w-4 mr-1" /> Valider
              </Button>
              <Button variant="outline" onClick={() => signatureRef.current?.clear()} className="text-sm">Effacer</Button>
              <Button variant="outline" onClick={() => { setShowSignaturePad(false); setPendingSignatureData(null); }} className="text-sm">Annuler</Button>
            </div>
          </div>
        </div>
      )}

      {/* Signature Pad Modal - Candidat */}
      {showCandidatSignPad && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" data-testid="candidat-signature-modal">
          <div className="bg-white rounded-xl p-6 w-[500px] max-w-[95vw]">
            <h4 className="font-bold text-slate-800 mb-1">Signature du candidat</h4>
            <p className="text-xs text-slate-500 mb-3">{selectedStagiaire?.prenom} {selectedStagiaire?.nom}</p>
            <div className="border-2 border-slate-200 rounded-lg overflow-hidden bg-white">
              <SignatureCanvas ref={candidatSignRef} canvasProps={{ width: 460, height: 180, className: 'signature-canvas' }} backgroundColor="white" />
            </div>
            <div className="flex gap-2 mt-3">
              <Button onClick={() => {
                if (candidatSignRef.current?.isEmpty()) { toast.error('Veuillez dessiner la signature'); return; }
                setCandidatSignature(candidatSignRef.current.toDataURL());
                setShowCandidatSignPad(false);
                toast.success('Signature du candidat enregistrée');
              }} className="bg-[#8DC63F] hover:bg-[#7db336] text-sm" data-testid="save-candidat-signature-btn">
                <Check className="h-4 w-4 mr-1" /> Valider
              </Button>
              <Button variant="outline" onClick={() => candidatSignRef.current?.clear()} className="text-sm">Effacer</Button>
              <Button variant="outline" onClick={() => setShowCandidatSignPad(false)} className="text-sm">Annuler</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
